import { ChangeEvent } from 'react';
import { Card, Input, Select } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import { PURPOSEOPTIONS } from 'components/addNewApplication/PurposeOfFundsHelper';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TDuration } from 'lendingApi/applications.types';
import { Controller, useFormContext } from 'react-hook-form';
import { REQUESTED_AMOUNT_CONTACT_THRESHOLD } from 'utils/applications/signup.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { TSignupFormData } from '../SignupForm/SignupForm';

import signupFormStyles from '../SignupForm/SignupForm.module.css';
import styles from './LoanApplication.module.css';

export const getLoanDuration = (productType?: string): TDuration | null => {
  switch (productType) {
    case 'recovery_loan':
      return '60-months';
    case 'flexi24':
      return '24-months';
    case 'credit_facility':
      return '12-months';
    default:
      return null;
  }
};

export const LoanApplication = () => {
  const { availableProducts } = useIntroducerContext();
  const { trackEvent } = useAmplitude();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<TSignupFormData>();
  const { checkIntroducerPermission } = useIntroducerContext();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  const handleProductTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) return;

    trackEvent(AMPLITUDE_EVENTS.productTypeSelected, {
      'Loan Duration': e.target.value,
    });
  };

  const handleLoanPurposeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    trackEvent(AMPLITUDE_EVENTS.loanPurposeSelected, {
      'Loan purpose': e.target.value,
    });
  };

  const aboveContactThreshold =
    Number(watch('amount')?.replace(/[^0-9]+/g, '')) >=
    REQUESTED_AMOUNT_CONTACT_THRESHOLD;

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>Loan request details</h2>
      <div className={signupFormStyles.cardContents}>
        <div className={signupFormStyles.formField}>
          <Controller
            name="amount"
            control={control}
            render={({ field: { onChange, value } }) => {
              const numericValue = value?.replace(/[^0-9]/g, '') ?? '';
              const formattedValue = numericValue
                ? parseInt(numericValue).toLocaleString()
                : '';

              return (
                <Input
                  name="amount"
                  className={styles.loanAmount}
                  label="Amount"
                  iconLeft="pound"
                  type="text"
                  disabled={isDisabled}
                  value={formattedValue}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  error={errors.amount?.message}
                />
              );
            }}
          />
          {aboveContactThreshold && !errors?.amount?.message && (
            <InformationMessage message="For amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance." />
          )}
        </div>
        <div className={signupFormStyles.formField}>
          {/* TODO: Update product type field to have extra features such as min-max text and introducer response check. */}
          <Select
            {...register('loanDuration')}
            onChange={handleProductTypeChange}
            label="Preferred term"
            defaultValue=""
            error={errors.loanDuration && errors.loanDuration.message}
            disabled={isDisabled}
          >
            <option value="" disabled>
              <Trans>Choose an option</Trans>
            </option>
            {availableProducts?.includes('credit_facility') && (
              <option value="12-months">12 months</option>
            )}
            {availableProducts?.includes('flexi24') && (
              <option value="24-months">24 months</option>
            )}
            {availableProducts?.includes('credit_facility') && (
              <>
                <option value="36-months">36 months</option>
                <option value="48-months">48 months</option>
                <option value="60-months">60 months</option>
              </>
            )}
          </Select>
        </div>
        <div className={signupFormStyles.formField}>
          <Select
            {...register('loanPurpose')}
            label="Purpose of the loan"
            onChange={handleLoanPurposeChange}
            error={errors.loanPurpose && errors.loanPurpose.message}
            defaultValue=""
            disabled={isDisabled}
          >
            <option value="" disabled>
              <Trans>Choose an option</Trans>
            </option>
            {PURPOSEOPTIONS()
              .filter((option) => option.enabledForUK)
              .map((option) => {
                return (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                );
              })}
          </Select>
        </div>
      </div>
    </Card>
  );
};
